import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, Clock, Play, Trash2 } from "lucide-react";
import { formatDateTime } from "@/lib/format";

const getBadgeVariant = (status) => {
  switch (status) {
    case 'created': return 'default';
    case 'streaming': return 'destructive';
    case 'completed': return 'secondary';
    case 'failed': return 'destructive';
    case 'scheduled': return 'default';
    case 'cancelled': return 'secondary';
    default: return 'default';
  }
};

const isUpcoming = (scheduledTime) => new Date(scheduledTime) > new Date();

const BroadcastsList = ({ broadcasts, onDelete, loading }) => {
  const activeBroadcasts = broadcasts.filter(b => b.status !== 'cancelled');
  const cancelledBroadcasts = broadcasts.filter(b => b.status === 'cancelled');

  const sortedActiveBroadcasts = [...activeBroadcasts].sort((a, b) => {
    const aUp = isUpcoming(a.scheduled_time);
    const bUp = isUpcoming(b.scheduled_time);
    if (aUp && !bUp) return -1;
    if (!aUp && bUp) return 1;
    return new Date(a.scheduled_time) - new Date(b.scheduled_time);
  });

  const sortedCancelledBroadcasts = [...cancelledBroadcasts].sort((a, b) =>
    new Date(b.scheduled_time) - new Date(a.scheduled_time)
  );

  if (!broadcasts || broadcasts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>No scheduled broadcasts yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sortedActiveBroadcasts.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Scheduled Broadcasts</h3>
          <div className="space-y-3">
            {sortedActiveBroadcasts.map((broadcast) => {
              const upcoming = isUpcoming(broadcast.scheduled_time);
              return (
                <Card key={broadcast.id} data-testid={`broadcast-card-${broadcast.id}`} className={upcoming ? '' : 'opacity-70 border-gray-200'}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-1">{broadcast.video_title}</h4>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            {formatDateTime(broadcast.scheduled_time)}
                          </div>
                          <Badge variant={getBadgeVariant(broadcast.status)}>
                            {upcoming ? 'Upcoming' : (broadcast.status === 'created' ? 'Completed' : broadcast.status)}
                          </Badge>
                        </div>
                        <div className="mt-2 flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => window.open(broadcast.watch_url, '_blank')} data-testid={`watch-button-${broadcast.id}`}>
                            <Play className="w-4 h-4 mr-1" />
                            Watch Page
                          </Button>
                          <div className="text-xs text-gray-500 flex items-center">Auto-stream enabled</div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => onDelete(broadcast.id)} disabled={loading} className="ml-2 text-red-600 hover:text-red-700" data-testid={`delete-button-${broadcast.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {sortedCancelledBroadcasts.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-600">Cancelled Broadcasts</h3>
          <div className="space-y-3">
            {sortedCancelledBroadcasts.map((broadcast) => (
              <Card key={broadcast.id} data-testid={`broadcast-card-${broadcast.id}`} className="opacity-60 border-gray-200 bg-gray-50">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-700 mb-1">{broadcast.video_title}</h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {formatDateTime(broadcast.scheduled_time)}
                        </div>
                        <Badge variant="secondary">Cancelled</Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BroadcastsList;
